/**
 * Mutation toast options wrapper for tRPC React mutations.
 * - Wrap your useMutation options: api.foo.bar.useMutation(withToast({ successMessage }))
 * - Shows success/error toasts using react-toastify.
 */
"use client";

import { toast } from "react-toastify";
import type { UseTRPCMutationOptions } from "@trpc/react-query/shared";

function extractMessage(error: unknown): string {
  if (typeof error === "string") return error;
  if (error && typeof error === "object") {
    const anyErr = error as { message?: string; data?: { zodError?: unknown } };
    if (anyErr?.data?.zodError) return "Invalid input.";
    if (anyErr?.message) return anyErr.message;
  }
  return "Something went wrong.";
}

export function withToast<
  TData = unknown,
  TError = unknown,
  TVariables = unknown,
  TContext = unknown,
>(
  opts?: UseTRPCMutationOptions<TData, TError, TVariables, TContext> & {
    successMessage?: string;
  },
): UseTRPCMutationOptions<TData, TError, TVariables, TContext> {
  const onSuccess = (...args: unknown[]) => {
    if (opts?.successMessage) toast.success(opts.successMessage);
    (opts as any)?.onSuccess?.(...(args as any));
  };
  const onError = (...args: unknown[]) => {
    const [error] = args;
    const msg = extractMessage(error as unknown);
    toast.error(msg);
    (opts as any)?.onError?.(...(args as any));
  };
  return {
    ...(opts as any),
    onSuccess: onSuccess as any,
    onError: onError as any,
  } as UseTRPCMutationOptions<TData, TError, TVariables, TContext>;
}
